import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { formatLabel, toIsoUtc } from "../labels";
import type {
  Department,
  KnowledgeBaseItemType,
} from "../types";

const ITEM_TYPES: KnowledgeBaseItemType[] = [
  "sop",
  "policy",
  "training",
  "playbook",
  "emergency_procedure",
  "template",
];

const DEPARTMENTS: Department[] = [
  "system_administration",
  "operations",
  "finance",
  "customer_support",
  "trust_and_safety",
  "marketing",
  "creator_operations",
  "marketplace_health",
];

export function ConfirmActionDialog({
  open,
  title,
  notice,
  actions,
  requireReason = false,
  extraFields,
  confirmVariant = "primary",
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireReason?: boolean;
  extraFields?: ReactNode;
  confirmVariant?: "primary" | "danger";
  onClose: () => void;
  onSubmit: (input: { action: string; reason?: string }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "";
  const [action, setAction] = useState(first);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
  }, [open, first]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={busy}
            disabled={
              !action || (requireReason && reason.trim().length < 3)
            }
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {extraFields}
        {requireReason ? (
          <Textarea
            label="Operator note (local confirmation only)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint="This endpoint does not accept a reason body; the note is for deliberate confirmation only and is not sent."
          />
        ) : null}
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateKnowledgeDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [itemType, setItemType] = useState<KnowledgeBaseItemType>("sop");
  const [contentReference, setContentReference] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [audienceScope, setAudienceScope] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [reviewDueAt, setReviewDueAt] = useState("");

  useEffect(() => {
    if (!open) return;
    setItemType("sop");
    setContentReference("");
    setOwnerUserId("");
    setAudienceScope("");
    setPolicyId("");
    setReviewDueAt("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Draft knowledge item"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!contentReference.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                item_type: itemType,
                content_reference: contentReference.trim(),
              };
              if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
              if (audienceScope.trim()) {
                body.audience_scope = audienceScope.trim();
              }
              if (policyId.trim()) body.policy_id = policyId.trim();
              if (reviewDueAt.trim()) {
                body.review_due_at = toIsoUtc(reviewDueAt);
              }
              void onSubmit(body);
            }}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Creates a draft Knowledge Base Item. content_reference points to
          governed copy — this UI does not edit live article bodies, generate
          AI content, or invent translations. There is no PATCH after create;
          lifecycle actions change status only.
        </Text>
        <Select
          label="Item type (category)"
          value={itemType}
          onChange={(event) =>
            setItemType(event.target.value as KnowledgeBaseItemType)
          }
          options={ITEM_TYPES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Textarea
          label="Content reference"
          value={contentReference}
          onChange={(event) => setContentReference(event.target.value)}
        />
        <Input
          label="Audience scope (visibility)"
          value={audienceScope}
          onChange={(event) => setAudienceScope(event.target.value)}
          hint="Opaque scope string stored on the record."
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Input
          label="Linked Policy ID (optional)"
          value={policyId}
          onChange={(event) => setPolicyId(event.target.value)}
        />
        <Input
          label="Review due at (optional)"
          type="datetime-local"
          value={reviewDueAt}
          onChange={(event) => setReviewDueAt(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreatePolicyDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [policyKey, setPolicyKey] = useState("");
  const [department, setDepartment] =
    useState<Department>("operations");
  const [ownerUserId, setOwnerUserId] = useState("");

  useEffect(() => {
    if (!open) return;
    setPolicyKey("");
    setDepartment("operations");
    setOwnerUserId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create policy"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!policyKey.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                policy_key: policyKey.trim(),
                owning_department: department,
              };
              if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
              void onSubmit(body);
            }}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Policies are governed documents with version history. Activate
          requires a version_id. Independent from Support tickets.
        </Text>
        <Input
          label="Policy key"
          value={policyKey}
          onChange={(event) => setPolicyKey(event.target.value)}
        />
        <Select
          label="Owning department"
          value={department}
          onChange={(event) =>
            setDepartment(event.target.value as Department)
          }
          options={DEPARTMENTS.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreatePolicyVersionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [version, setVersion] = useState("");
  const [contentReference, setContentReference] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  useEffect(() => {
    if (!open) return;
    setVersion("");
    setContentReference("");
    setEffectiveFrom("");
    setEffectiveTo("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add policy version"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!version.trim() || !contentReference.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                version: version.trim(),
                content_reference: contentReference.trim(),
              };
              if (effectiveFrom.trim()) {
                body.effective_from = toIsoUtc(effectiveFrom);
              }
              if (effectiveTo.trim()) {
                body.effective_to = toIsoUtc(effectiveTo);
              }
              void onSubmit(body);
            }}
          >
            Create version
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Versions are the revision history for a Policy. content_reference is
          a pointer — not an in-app document editor.
        </Text>
        <Input
          label="Version label"
          value={version}
          onChange={(event) => setVersion(event.target.value)}
        />
        <Textarea
          label="Content reference"
          value={contentReference}
          onChange={(event) => setContentReference(event.target.value)}
        />
        <Input
          label="Effective from (optional)"
          type="datetime-local"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
        />
        <Input
          label="Effective to (optional)"
          type="datetime-local"
          value={effectiveTo}
          onChange={(event) => setEffectiveTo(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
